import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { itemPath, KIND_LABELS } from "../../utilities/item-kinds";
import FramedImage from "../../components/FramedImage/FramedImage";
import { thumbUrl } from "../../utilities/image-urls";

const AUTO_ADVANCE_MS = 7000;

// Past this carousel zoom the 2048px thumbnail can't fill the hero window
// (663×386 CSS px at the widest breakpoint) without upscaling, so the
// original is used instead. Cover-cropping needs 663 × zoom × DPR px of
// source width, which at DPR 2 hits 2048 around 1.5x.
//
// That 1.5 holds for landscape scans, where the thumb is ~2048×1190. The
// binding dimension is really the thumb's SHORT edge, and the long edge is
// always capped at 2048 — so a portrait scan (~1450–1870px wide) is only
// safe to ~1.1–1.4x. Every portrait item currently sits at carousel zoom
// 1.0 and only landscape covers are featured, so nothing is affected today;
// a portrait item featured above ~1.1x would render slightly soft, and
// FramedImage can't detect that (it only falls back on a 404). Storing
// image dimensions on the item would let this be exact.
const MAX_THUMB_ZOOM = 1.5;

function carouselSrc(item) {
  const zoom = item.framing?.carousel?.zoom ?? 1;
  return zoom <= MAX_THUMB_ZOOM ? thumbUrl(item.image) : null;
}

export default function FeaturedCarousel({ items }) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

  // Auto-advance; restarts whenever the slide changes (manual or automatic)
  useEffect(() => {
    if (count < 2) return;
    const timer = setInterval(next, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [next, count, index]);

  if (count === 0) return null;
  const item = items[Math.min(index, count - 1)];

  return (
    <div className="flex items-center gap-2 sm:gap-4">
      <button
        onClick={() => setIndex((i) => (i - 1 + count) % count)}
        aria-label="Previous"
        className="shrink-0 text-2xl text-ink/40 hover:text-ink transition-colors px-1"
      >
        &#8249;
      </button>

      <div className="flex-1 min-w-0">
        {/* Window keeps FDC proportions (1280×745); the per-item carousel
            framing controls how the scan fills it */}
        <div className="relative overflow-hidden bg-cream border border-ink/10 rounded-md shadow-carousel aspect-[1280/745]">
          <Link
            to={itemPath(item)}
            state={{ fromFeed: true }}
            className="group absolute inset-0"
          >
            <FramedImage
              key={item._id}
              src={item.image}
              thumbSrc={carouselSrc(item)}
              alt={item.subject || "Featured item"}
              framing={item.framing?.carousel}
              className="animate-fadeIn"
            />
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 max-w-[85%] truncate rounded-full bg-paper/90 border border-ink/10 px-4 py-1.5 font-mono text-xs text-ink shadow-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {item.subject || KIND_LABELS[item.kind]}
            </span>
          </Link>
        </div>

        {count > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            {items.map((it, i) => (
              <button
                key={it._id}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === index ? "bg-seal" : "bg-ink/15 hover:bg-ink/30"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <button
        onClick={next}
        aria-label="Next"
        className="shrink-0 text-2xl text-ink/40 hover:text-ink transition-colors px-1"
      >
        &#8250;
      </button>
    </div>
  );
}
